import React, { useState, useEffect, useCallback } from "react";
import { Image, Popover, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";
import PhotoPopover from "./PhotoPopover";

// Простейший кеш картинок в памяти, чтобы не дёргать API при каждом рендере
// Ключ: `${entity}:${entityId}` → массив картинок
const picturesCache = new Map();

const getPicturesCacheKey = (entity, entityId) =>
  entity && (entityId || entityId === 0) ? `${entity}:${entityId}` : null;

const PhotoCell = ({ entity, entityId, token, size = 45, messageApi, fallbackEntity, fallbackEntityId }) => {
  const [pictures, setPictures] = useState([]);
  // Инициализируем loading в false только если есть все необходимые данные
  const [loading, setLoading] = useState(() => {
    // Если нет необходимых данных, сразу false, чтобы показать кнопку
    return !!(entity && entityId !== undefined && entityId !== null && token);
  });
  const [popoverVisible, setPopoverVisible] = useState(false);

  const loadPictures = useCallback(
    async (force = false) => {
    // Проверяем, что все необходимые данные присутствуют
    if (!entity || entityId === undefined || entityId === null || !token) {
      setLoading(false);
      setPictures([]);
      return;
    }

      const cacheKey = getPicturesCacheKey(entity, entityId);
      if (!force && cacheKey && picturesCache.has(cacheKey)) {
        setPictures(picturesCache.get(cacheKey));
        setLoading(false);
        return;
      }

    try {
      setLoading(true);
      
      // Добавляем таймаут для запроса, чтобы избежать зависания
      // Уменьшаем таймаут до 3 секунд для категорий, чтобы быстрее показывать кнопку
      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Request timeout')), 3000);
      });
      
      let response;
      try {
        response = await Promise.race([
          apiRequests.pictures.getItemsOrder(entity, entityId, token),
          timeoutPromise
        ]);
        // Очищаем таймаут, если запрос успешно завершился
        if (timeoutId) clearTimeout(timeoutId);
      } catch (raceError) {
        // Очищаем таймаут при ошибке
        if (timeoutId) clearTimeout(timeoutId);
        throw raceError;
      }

      // Обрабатываем разные форматы ответа
      let picturesData = [];
      if (Array.isArray(response.data)) {
        picturesData = response.data;
      } else if (response.data?.result && Array.isArray(response.data.result)) {
        picturesData = response.data.result;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        picturesData = response.data.data;
      }

      if (picturesData.length > 0) {
        const allImages = picturesData.map((item) => {
          // Проверяем, если url уже полный, используем его, иначе формируем
          let imageUrl = item.url;
          if (imageUrl && !imageUrl.startsWith('http')) {
            // Убираем ведущий слэш если есть
            const cleanUrl = imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl;
            imageUrl = `https://${process.env.REACT_APP_APP_URL || 'app.tablecrm.com'}/api/v1/${cleanUrl}/`;
          }
          return {
            id: item.id,
            name: item.url || item.name || `image_${item.id}`,
            url: imageUrl,
          };
        });
        setPictures(allImages);
        const cacheKey = getPicturesCacheKey(entity, entityId);
        if (cacheKey) {
          picturesCache.set(cacheKey, allImages);
        }
      } else {
        // Если нет фотографий у основной сущности, пробуем загрузить из fallback
        if (fallbackEntity && fallbackEntityId) {
          try {
            const fallbackResponse = await apiRequests.pictures.getItemsOrder(
              fallbackEntity,
              fallbackEntityId,
              token
            );
            
            let fallbackPicturesData = [];
            if (Array.isArray(fallbackResponse.data)) {
              fallbackPicturesData = fallbackResponse.data;
            } else if (fallbackResponse.data?.result && Array.isArray(fallbackResponse.data.result)) {
              fallbackPicturesData = fallbackResponse.data.result;
            } else if (fallbackResponse.data?.data && Array.isArray(fallbackResponse.data.data)) {
              fallbackPicturesData = fallbackResponse.data.data;
            }
            
            if (fallbackPicturesData.length > 0) {
              const fallbackImages = fallbackPicturesData.map((item) => {
                let imageUrl = item.url;
                if (imageUrl && !imageUrl.startsWith('http')) {
                  const cleanUrl = imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl;
                  imageUrl = `https://${process.env.REACT_APP_APP_URL || 'app.tablecrm.com'}/api/v1/${cleanUrl}/`;
                }
                return {
                  id: item.id,
                  name: item.url || item.name || `image_${item.id}`,
                  url: imageUrl,
                };
              });
              setPictures(fallbackImages);
              const cacheKey = getPicturesCacheKey(
                fallbackEntity,
                fallbackEntityId
              );
              if (cacheKey) {
                picturesCache.set(cacheKey, fallbackImages);
              }
            } else {
              setPictures([]);
            }
          } catch (fallbackError) {
            // Игнорируем ошибки при загрузке fallback фотографий
            setPictures([]);
          }
        } else {
          setPictures([]);
        }
      }
    } catch (error) {
      // Определяем тип ошибки
      const status = error?.response?.status;
      const isTimeout = error?.message === 'Request timeout';
      const isNetworkError = error?.isNetworkError || 
                            error?.code === 'ERR_NETWORK' || 
                            error?.code === 'ERR_INSUFFICIENT_RESOURCES' ||
                            error?.message === 'Network Error' ||
                            !error?.response;
      
      // Логируем только не-сетевые ошибки, чтобы не засорять консоль
      if (!isNetworkError && !isTimeout && status !== 404) {
        console.error("Ошибка загрузки изображений:", error);
      }
      
      // Не показываем ошибку для:
      // - отсутствия данных (404)
      // - таймаута
      // - сетевых ошибок (CORS, нет подключения, недостаточно ресурсов и т.д.)
      if (status !== 404 && status !== 200 && !isTimeout && !isNetworkError) {
        const errorMessage = 
          error?.response?.data?.message || 
          error?.response?.data?.detail || 
          error?.message || 
          "Ошибка при загрузке изображений";
        
        // Используем messageApi если передан, иначе обычный message
        if (messageApi) {
          messageApi.open({ type: 'error', content: errorMessage, duration: 4 });
        } else {
          message.error(errorMessage);
        }
      }
      
      setPictures([]);
    } finally {
      setLoading(false);
    }
  },
  [entity, entityId, token, messageApi, fallbackEntity, fallbackEntityId]
);

  const handleRefresh = useCallback(() => {
    // Принудительно обновляем кеш и перезагружаем картинки
    const cacheKey = getPicturesCacheKey(entity, entityId);
    if (cacheKey && picturesCache.has(cacheKey)) {
      picturesCache.delete(cacheKey);
    }
    loadPictures(true);
  }, [entity, entityId, loadPictures]);

  useEffect(() => {
    // Используем entityId как зависимость, чтобы избежать бесконечных циклов
    // и перезагружать только при изменении entityId
    if (entity && entityId !== undefined && entityId !== null && token) {
      loadPictures();
    } else {
      setLoading(false);
      setPictures([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, entityId, token]);

  // Слушаем глобальные события обновления картинок,
  // чтобы подтягивать фото после загрузки из пошагового мастера и других мест
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (event) => {
      const detail = event?.detail || {};
      if (
        detail.entity === entity &&
        (detail.entityId === entityId ||
          String(detail.entityId) === String(entityId))
      ) {
        handleRefresh();
      }
    };

    window.addEventListener("pictures-updated", handler);
    return () => {
      window.removeEventListener("pictures-updated", handler);
    };
  }, [entity, entityId, handleRefresh]);

  // Пустая ячейка для добавления фото
  const emptyCell = (
    <div
      style={{
        width: size,
        height: size,
        border: "1px dashed #d9d9d9",
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        backgroundColor: "#fafafa",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#1890ff";
        e.currentTarget.style.backgroundColor = "#f0f8ff";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#d9d9d9";
        e.currentTarget.style.backgroundColor = "#fafafa";
      }}
    >
      <PlusOutlined style={{ color: "#8c8c8c", fontSize: "16px" }} />
    </div>
  );

  // Используем фотки текущего товара
  const displayPictures = pictures;

  // Ячейка с фото (без немедленного полноэкранного просмотра —
  // сначала открываем поповер с галереей, а уже оттуда — полноэкранный просмотр)
  const photoCell = displayPictures.length > 0 ? (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        cursor: "pointer",
        borderRadius: "4px",
        overflow: "hidden",
        border: "1px solid #f0f0f0",
      }}
      >
        <Image
          src={displayPictures[0].url}
          alt={displayPictures[0].name}
          width={size}
          height={size}
          style={{
            objectFit: "cover",
          }}
        preview={false}
        />
      {displayPictures.length > 1 && (
        <div
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            backgroundColor: "#ff4d4f",
            color: "white",
            borderRadius: "10px",
            minWidth: "18px",
            height: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "11px",
            fontWeight: 500,
            padding: "0 5px",
            zIndex: 10,
            pointerEvents: "none",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          {displayPictures.length}
        </div>
      )}
    </div>
  ) : null;

  const content = (
    <PhotoPopover
      pictures={pictures}
      entity={entity}
      entityId={entityId}
      token={token}
      onRefresh={handleRefresh}
      onClose={() => setPopoverVisible(false)}
      messageApi={messageApi}
      fallbackEntity={fallbackEntity}
      fallbackEntityId={fallbackEntityId}
    />
  );

  // Если нет необходимых данных, сразу показываем emptyCell
  if (!entity || entityId === undefined || entityId === null || !token) {
    return (
      <Popover
        content={content}
        trigger="click"
        placement="bottomLeft"
        open={popoverVisible}
        onOpenChange={setPopoverVisible}
        overlayStyle={{ padding: 0 }}
        overlayInnerStyle={{ padding: 0 }}
      >
        <div
          onClick={() => setPopoverVisible(!popoverVisible)}
          style={{ cursor: "pointer" }}
        >
          {emptyCell}
        </div>
      </Popover>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px dashed #d9d9d9",
          borderRadius: "4px",
          fontSize: "11px",
          color: "#8c8c8c",
        }}
      >
        ...
      </div>
    );
  }

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomLeft"
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
      overlayStyle={{ padding: 0 }}
      overlayInnerStyle={{ padding: 0 }}
    >
      <div
        onClick={() => setPopoverVisible(!popoverVisible)}
        style={{ cursor: "pointer" }}
      >
        {photoCell || emptyCell}
      </div>
    </Popover>
  );
};

export default PhotoCell;

